import type {
  BaseTokenInfo,
  TokenInfo,
} from "@src/components/DefuseSDK/types/base"
import { LIST_TOKENS, LIST_TOKENS_FLATTEN } from "@src/constants/tokens"
import type { Holding } from "../features/account/types/sharedTypes"
import { isBaseToken } from "./token"
import { isEvmAddress } from "./validateAddress"
const SEPARATOR = ":"

/**
 * Finds the ETH token in the list, deterministically:
 *   1. the unified (multi-chain) entry, if present (network stays unselected
 *      under the network-safety model);
 *   2. otherwise ETH on Ethereum mainnet (flattened lists carry ETH on several
 *      chains — pin to `eth` rather than whichever sorts first);
 *   3. otherwise any ETH.
 */
function findEthToken(tokenList: TokenInfo[]): TokenInfo | null {
  const isEth = (t: TokenInfo) => t.symbol.toUpperCase() === "ETH"
  return (
    tokenList.find((t) => !isBaseToken(t) && isEth(t)) ??
    tokenList.find(
      (t) => isBaseToken(t) && isEth(t) && t.originChainName === "eth"
    ) ??
    tokenList.find(isEth) ??
    null
  )
}

/**
 * Parses a token identifier from URL (e.g. "USDC" or "USDC:solana").
 * Returns the matching base token or null.
 */
export function parseTokenFromUrl(
  symbolWithOptionalChain: string | null | undefined
): BaseTokenInfo | null {
  if (!symbolWithOptionalChain?.trim()) return null

  const [symbol, chainName] = symbolWithOptionalChain
    .trim()
    .split(SEPARATOR) as [string, string | undefined]

  if (chainName === undefined) {
    const found = LIST_TOKENS.find((t) => t.symbol === symbol)
    if (found == null) return null
    return isBaseToken(found) ? found : (found.groupedTokens?.[0] ?? null)
  }

  return (
    LIST_TOKENS_FLATTEN.find(
      (t) => t.symbol === symbol && t.originChainName === chainName
    ) ?? null
  )
}

/**
 * Finds a token by symbol in the provided token list.
 * Uses the same approach as swap page's useDeterminePair.
 * For symbol-only input (no chain), prefers unified tokens.
 * For symbol:chain input, finds the specific base token.
 */
export function findTokenBySymbol(
  input: string | null | undefined,
  tokens: TokenInfo[]
): TokenInfo | null {
  if (!input?.trim()) return null

  const trimmedInput = input.trim()

  // If input contains chain separator, find specific base token
  if (trimmedInput.includes(SEPARATOR)) {
    const [symbol, chainName] = trimmedInput.split(SEPARATOR) as [
      string,
      string | undefined,
    ]
    const baseToken = LIST_TOKENS_FLATTEN.find(
      (t) => t.symbol === symbol && t.originChainName === chainName
    )
    if (!baseToken) return null
    // Return the TokenInfo from the list that contains this base token
    return findTokenInListByBase(baseToken, tokens)
  }

  // For symbol-only, explicitly prefer unified tokens over base tokens
  const symbolLower = trimmedInput.toLowerCase()

  // First, try to find a unified token in the provided tokens list
  const unifiedTokenInList = tokens.find(
    (token) =>
      !isBaseToken(token) &&
      (token.symbol.toLowerCase() === symbolLower ||
        token.groupedTokens?.some(
          (t: BaseTokenInfo) => t.symbol.toLowerCase() === symbolLower
        ))
  )

  if (unifiedTokenInList) {
    return unifiedTokenInList
  }

  // The provided tokens list might be flattened (no unified tokens).
  // Search in LIST_TOKENS which contains unified tokens.
  const unifiedTokenInListTokens = LIST_TOKENS.find(
    (token) =>
      !isBaseToken(token) &&
      (token.symbol.toLowerCase() === symbolLower ||
        token.groupedTokens?.some(
          (t: BaseTokenInfo) => t.symbol.toLowerCase() === symbolLower
        ))
  )

  if (unifiedTokenInListTokens) {
    return unifiedTokenInListTokens
  }

  // Fallback to base token if no unified token found anywhere
  return (
    tokens.find((token) => token.symbol.toLowerCase() === symbolLower) ?? null
  )
}

/**
 * Finds the TokenInfo in tokenList that represents the given base token
 * (either the base itself or a grouped token containing it).
 */
export function findTokenInListByBase(
  baseToken: BaseTokenInfo,
  tokenList: TokenInfo[]
): TokenInfo | null {
  return (
    tokenList.find((t) => {
      if (isBaseToken(t)) return t.defuseAssetId === baseToken.defuseAssetId
      return t.groupedTokens?.some(
        (bt) => bt.defuseAssetId === baseToken.defuseAssetId
      )
    }) ?? null
  )
}

/**
 * From holdings (sorted by USD value), returns the first token matching
 * the given symbol. Because holdings are pre-sorted by value, this
 * effectively picks the deployment with the highest balance.
 */
export function resolveHighestBalanceToken(
  symbol: string,
  holdings: Holding[]
): TokenInfo | null {
  const symbolLower = symbol.toLowerCase()
  const match = holdings.find(
    (h) => h.token.symbol.toLowerCase() === symbolLower
  )
  return match?.token ?? null
}

/**
 * Resolves the initial tokenIn for the swap page.
 * Priority: URL param (balance-aware) > highest-balance holding > default > first in list.
 */
export function resolveInitialTokenIn({
  fromParam,
  holdingToken,
  defaultTokenIn,
  tokenList,
  holdings,
}: {
  fromParam: string | null
  holdingToken: TokenInfo | null
  defaultTokenIn: TokenInfo | null | undefined
  tokenList: TokenInfo[]
  holdings: Holding[]
}): TokenInfo | undefined {
  const preset = fromParam?.trim()
  if (!preset) {
    return holdingToken ?? defaultTokenIn ?? tokenList[0] ?? undefined
  }

  const matched = findTokenBySymbol(preset, tokenList)
  if (matched == null) return defaultTokenIn ?? tokenList[0] ?? undefined

  // Swap uses flattened token list (1cs). For symbol-only presets (e.g. "USDC"),
  // pick the deployment with the highest balance instead of a unified token.
  if (!isBaseToken(matched)) {
    return (
      resolveHighestBalanceToken(matched.symbol, holdings) ??
      defaultTokenIn ??
      tokenList[0] ??
      undefined
    )
  }

  return matched
}

/**
 * Resolves the initial tokenOut for the swap page.
 * For symbol-only URL params, uses balance-aware resolution.
 */
export function resolveInitialTokenOut({
  toParam,
  tokenOut,
  holdings,
}: {
  toParam: string | null
  tokenOut: TokenInfo | null | undefined
  holdings: Holding[]
}): TokenInfo | undefined {
  if (!tokenOut) return undefined
  // Only apply balance-aware resolution for symbol-only URL params
  if (toParam && !toParam.includes(":")) {
    return resolveHighestBalanceToken(tokenOut.symbol, holdings) ?? tokenOut
  }
  return tokenOut
}

/**
 * Resolves the initial token for the withdraw/send page.
 * Priority: exact preset > symbol preset (balance-aware) > recipient-coherent
 * default (EVM recipient → ETH) > highest holding > first in list.
 */
export function resolveWithdrawInitialToken({
  presetToken,
  tokenList,
  holdings,
  recipient,
}: {
  presetToken: string | undefined
  tokenList: TokenInfo[]
  holdings: Holding[] | undefined
  /**
   * The destination address, when one is already known (e.g. a QR scan that
   * pre-filled the recipient but no token). Used to keep the default token
   * chain-coherent: a bare EVM `0x…` recipient defaults to ETH instead of the
   * largest holding, which could otherwise pair an incompatible token+chain
   * (e.g. Zcash-only ZEC) with an EVM address — a funds-safety bug.
   */
  recipient?: string | undefined
}): TokenInfo | undefined {
  const preset = presetToken?.trim()

  if (!preset) {
    // Recipient known but no token chosen: prefer a token compatible with the
    // recipient's chain over the largest holding. EVM addresses are valid on
    // every EVM chain, so default to the (multi-chain) ETH token and let the
    // user pick the specific network. Crucially, an EVM recipient never falls
    // back to a holding — that could pair a chain-incompatible token (e.g.
    // Zcash-only ZEC) with an EVM address. The app default is used instead.
    if (recipient && isEvmAddress(recipient.trim())) {
      return findEthToken(tokenList) ?? tokenList[0]
    }
    return holdings?.[0]?.token ?? tokenList[0]
  }

  // Exact match (e.g. "USDC:solana")
  if (preset.includes(":")) {
    return findTokenBySymbol(preset, tokenList) ?? tokenList[0]
  }

  // Symbol-only preset — balance-aware resolution
  if (holdings != null && holdings.length > 0) {
    const best = resolveHighestBalanceToken(preset, holdings)
    if (best) return best
  }

  return findTokenBySymbol(preset, tokenList) ?? tokenList[0]
}

export function formatTokenUrlSymbol(token: TokenInfo): string {
  if (isBaseToken(token)) return `${token.symbol}:${token.originChainName}`
  return token.symbol
}
